import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Truck, FileText, Eye, Package, MapPin, CheckCircle2 } from "lucide-react";

const STATUS_COLORS = {
    placed: "bg-stone-100 text-stone-800",
    confirmed: "bg-sky-100 text-sky-800",
    packing: "bg-amber-100 text-amber-800",
    packed: "bg-amber-100 text-amber-800",
    shipped: "bg-emerald-100 text-emerald-800",
    delivered: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800",
};

const STATUS_LABEL = {
    placed: "Placed",
    confirmed: "Confirmed by HPL",
    packing: "Packing in progress",
    packed: "Packed — awaiting dispatch",
    shipped: "Shipped",
    delivered: "Delivered",
    cancelled: "Cancelled",
};

export default function DistributorOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/distributors/orders")
            .then((r) => setOrders(r.data || []))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="dist-orders" className="space-y-4">
            <div>
                <div className="label-eyebrow text-sky-700">History</div>
                <h1 className="font-serif text-4xl text-stone-900">My orders</h1>
                <p className="text-sm text-stone-600 mt-1">
                    Live dispatch status &amp; transport copies for every order you've placed.
                </p>
            </div>

            {loading ? (
                <div className="p-12 text-center text-stone-500">Loading…</div>
            ) : orders.length === 0 ? (
                <div className="bg-white border border-stone-200 p-8 text-center text-sm text-stone-500">No orders yet. Browse the catalog to place your first wholesale order.</div>
            ) : (
                <div className="space-y-4">
                    {orders.map((o) => {
                        const tc = o.transport_copy;
                        const itemCount = (o.items || []).reduce((s, x) => s + (x.quantity || 0), 0);
                        return (
                            <div key={o.id} className="bg-white border border-stone-200" data-testid={`dist-order-${o.id}`}>
                                <div className="flex items-center justify-between p-4 border-b border-stone-100 flex-wrap gap-2">
                                    <div>
                                        <Link to={`/distributor/orders/${o.id}`} className="font-mono text-sm text-sky-700 hover:underline" data-testid={`dist-order-link-${o.id}`}>
                                            #{o.id.slice(-8).toUpperCase()}
                                        </Link>
                                        <span className="text-xs text-stone-500 ml-3">{(o.created_at || "").slice(0, 10)}</span>
                                        {o.invoice_number && <span className="text-xs text-stone-500 ml-3 font-mono">Invoice {o.invoice_number}</span>}
                                    </div>
                                    <Badge className={`rounded-none text-[11px] tracking-widest uppercase ${STATUS_COLORS[o.status] || "bg-stone-100"}`}>
                                        {STATUS_LABEL[o.status] || o.status}
                                    </Badge>
                                </div>

                                <div className="p-4 space-y-2">
                                    {(o.items || []).map((i, idx) => (
                                        <div key={idx} className="flex justify-between text-sm">
                                            <span className="text-stone-700">{i.product_name || i.name} ({i.size_label}) × <strong>{i.quantity}</strong></span>
                                            <span className="text-stone-600 font-mono">{formatINR((i.price || 0) * (i.quantity || 0))}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between p-4 border-t border-stone-100 text-sm">
                                    <span className="text-xs text-stone-500"><strong>{itemCount}</strong> total units · {(o.items || []).length} products</span>
                                    <span className="font-medium font-mono">{formatINR(o.total)}</span>
                                </div>

                                {/* Transport copy area — visible the moment HPL uploads it */}
                                {tc && (
                                    <div className="bg-emerald-50 border-t border-emerald-200 p-4" data-testid={`dist-tc-${o.id}`}>
                                        <div className="flex items-start gap-3">
                                            <Truck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                                            <div className="flex-1">
                                                <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Shipped</div>
                                                <div className="text-sm text-emerald-900 mt-1">
                                                    {tc.transporter && <span className="font-medium">{tc.transporter}</span>}
                                                    {tc.lr_number && <span className="font-mono ml-2">LR {tc.lr_number}</span>}
                                                </div>
                                                {tc.notes && <div className="text-xs text-emerald-800 mt-1">{tc.notes}</div>}
                                                <a
                                                    href={tc.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center mt-2 text-sm text-emerald-700 hover:text-emerald-900 font-medium"
                                                    data-testid={`dist-tc-download-${o.id}`}
                                                >
                                                    <FileText className="w-4 h-4 mr-1.5" />
                                                    Download transport copy ({tc.file_name})
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="px-4 py-3 border-t border-stone-100 flex justify-end print:hidden">
                                    <Link to={`/distributor/orders/${o.id}`} className="text-xs text-sky-700 hover:underline inline-flex items-center" data-testid={`dist-order-view-${o.id}`}>
                                        <Eye className="w-3.5 h-3.5 mr-1" />View full details
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
